import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { themeColors } from '../../constants/design';
import questionnaire from '../../config/questionnaire';
import methodology from '../../config/methodology';
import { analyzeSentiment, summarizeSentiments } from '../../utils/sentiment';
import { buildQualitativeInsights } from '../../utils/insights';
import {
  aggregateShares,
  aggregateTrendShares,
  buildOperationalFriction,
  buildExecutiveSummary,
  buildNavieraPerformance,
  buildSegmentPerformance,
  getDimensionDrivers,
  getSubmissionNavieraDiagnostics,
} from '../../utils/dashboardMetrics';
import { getSectorVisual, getDestinationVisual, getPortVisual } from '../../utils/iconography';
import {
  AnchorIcon,
  TrendingUpIcon,
  AlertIcon,
} from '../icons';
import { LogoBlue } from '../ui/Logo';
import { ScoreArcGauge, MiniScoreRing } from '../ui/Gauges';
import AiNarrativeReportSection from './AiNarrativeReportSection';
import TrendSection from './TrendSection';
import { generateExecutivePDF } from '../../utils/generatePDF';
import {
  firebaseEnabled,
  generateReportNarrative as generateFirebaseReportNarrative,
} from '../../services/firebase';

const DIMENSION_LABEL_MAP = {
  'Disponibilidad Logística': 'Disponibilidad',
  Costos: 'Costos',
  'Comunicación y Cumplimiento Naviero': 'Cumplimiento',
  'Operación Portuaria': 'Operación',
  'Experiencia General': 'Experiencia',
};

const NAVIERA_METRIC_DESCRIPTORS = [
  { key: 'quality', label: 'Calidad', description: 'Percepción de servicio', accent: 'text-sky-600' },
  { key: 'agility', label: 'Agilidad', description: 'Tiempo de respuesta', accent: 'text-emerald-600' },
  { key: 'costs', label: 'Costos', description: 'Eficiencia tarifaria', accent: 'text-amber-600' },
  { key: 'availability', label: 'Disponibilidad', description: 'Cupos y flota', accent: 'text-indigo-600' },
];

const SCORE_BANDS = [
  {
    min: 85,
    range: '85-100',
    label: 'Excelente',
    helper: 'desempeño alto',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
  {
    min: 70,
    range: '70-84',
    label: 'Bueno',
    helper: 'cumple bien',
    dot: 'bg-lime-500',
    bar: 'bg-lime-500',
    text: 'text-lime-700',
    bg: 'bg-lime-50',
    border: 'border-lime-200',
  },
  {
    min: 55,
    range: '55-69',
    label: 'Aceptable',
    helper: 'brechas manejables',
    dot: 'bg-amber-400',
    bar: 'bg-amber-400',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  {
    min: 40,
    range: '40-54',
    label: 'Alerta',
    helper: 'requiere gestión',
    dot: 'bg-orange-500',
    bar: 'bg-orange-500',
    text: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  {
    min: 0,
    range: '0-39',
    label: 'Crítico',
    helper: 'fricción severa',
    dot: 'bg-rose-500',
    bar: 'bg-rose-500',
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
  },
];

const RISK_BANDS = [
  {
    min: 65,
    range: '65-100',
    label: 'Riesgo crítico',
    dot: 'bg-rose-500',
    bar: 'bg-rose-500',
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
  },
  {
    min: 45,
    range: '45-64',
    label: 'Riesgo alto',
    dot: 'bg-orange-500',
    bar: 'bg-orange-500',
    text: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  {
    min: 25,
    range: '25-44',
    label: 'Riesgo medio',
    dot: 'bg-amber-400',
    bar: 'bg-amber-400',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  {
    min: 0,
    range: '0-24',
    label: 'Riesgo bajo',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
  },
];

const clampScoreValue = (value) => Math.max(0, Math.min(100, Number(value) || 0));
const getScoreBand = (score) => SCORE_BANDS.find((band) => clampScoreValue(score) >= band.min) || SCORE_BANDS[SCORE_BANDS.length - 1];
const getRiskBand = (score) => RISK_BANDS.find((band) => clampScoreValue(score) >= band.min) || RISK_BANDS[RISK_BANDS.length - 1];

const SEGMENT_TONE_STYLES = {
  blue: { accent: 'bg-[#004AAD]', soft: 'bg-[#004AAD]/5', border: 'border-[#004AAD]/20', text: 'text-[#004AAD]' },
  teal: { accent: 'bg-teal-500', soft: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700' },
  amber: { accent: 'bg-amber-500', soft: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
};

const formatMetricValue = (value, suffix = '') => {
  const numeric = Number(value) || 0;
  const formatted = Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(1);
  return `${formatted}${suffix}`;
};

const REPORT_VIEWS = [
  { id: 'executive', label: 'Ejecutivo' },
  { id: 'extended', label: 'Extendido' },
];

const copyCanvasElements = (sourceNode, clonedNode) => {
  const sourceCanvases = Array.from(sourceNode.querySelectorAll('canvas'));
  const clonedCanvases = Array.from(clonedNode.querySelectorAll('canvas'));

  sourceCanvases.forEach((sourceCanvas, index) => {
    const clonedCanvas = clonedCanvases[index];
    if (!clonedCanvas) return;

    clonedCanvas.width = sourceCanvas.width;
    clonedCanvas.height = sourceCanvas.height;

    const context = clonedCanvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, clonedCanvas.width, clonedCanvas.height);
    context.drawImage(sourceCanvas, 0, 0);
  });
};

const EXECUTIVE_PDF_PAGE_SOURCES = [
  ['hero', 'summary'],
  ['trend', 'indices'],
  ['navieras', 'sectors'],
  ['segments'],
  ['drivers', 'destinations'],
];

const EXECUTIVE_PAGE_SOURCE_BREAKS = {
  summary: true,
  trend: true,
  indices: true,
  ai_narrative: true,
  navieras: true,
  sectors: true,
  segments: true,
};

const PDF_PAGE_SOURCE_BREAKS = {
  ...EXECUTIVE_PAGE_SOURCE_BREAKS,
  'extended-intro': true,
};

const ENABLE_OTHERS_SECTOR_HOVER_DETAILS = true;

const roundReportMetric = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(2)) : null
);

const averageReportMetric = (values) => {
  const valid = values.filter((value) => typeof value === 'number' && Number.isFinite(value));
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const getExecutiveInsightTone = (score = 0) => {
  if (score < 40) {
    return {
      label: 'Crítico',
      frame: 'border-rose-200 bg-rose-50/60',
      accent: 'bg-rose-500',
      text: 'text-rose-600',
      pill: 'border-rose-100 bg-white text-rose-600',
      bar: 'bg-rose-500',
    };
  }

  return {
    label: 'Alerta',
    frame: 'border-amber-200 bg-amber-50/60',
    accent: 'bg-amber-500',
    text: 'text-amber-600',
    pill: 'border-amber-100 bg-white text-amber-600',
    bar: 'bg-amber-500',
  };
};

const getExecutiveInsightAction = ({ dimension = '', question = '' }) => {
  const text = `${dimension} ${question}`.toLowerCase();
  if (/costo|tarif|cargo|flete|margen/.test(text)) {
    return 'Auditar cargos adicionales, justificación tarifaria y variación por naviera.';
  }
  if (/puerto|portuaria|congest|infraestructura|aduaner|inspeccion|acceso/.test(text)) {
    return 'Separar el análisis por puerto y escalar fricciones operativas recurrentes.';
  }
  if (/contenedor|espacio|cupo|flota|disponibilidad|reserv/.test(text)) {
    return 'Contrastar disponibilidad real, cupos y anticipación de reservas por naviera.';
  }
  if (/comunic|respuesta|cumplimiento|document|tracking|trazabilidad/.test(text)) {
    return 'Revisar tiempos de respuesta, cumplimiento de promesas y trazabilidad.';
  }
  return 'Cruzar esta señal con verbatims, navieras evaluadas y recurrencia mensual.';
};

const buildExecutiveInsight = (item, index, type = 'alert') => {
  const value = typeof item.value === 'number' ? item.value : 0;
  const tone = getExecutiveInsightTone(value);
  const question = item.text || item.question || 'Señal del levantamiento';
  const dimension = item.dimension || item.name || 'Dimensión';

  return {
    id: `${type}-${item.id || dimension}-${index}`,
    type,
    dimension,
    question,
    value,
    severity: tone.label,
    tone,
    action: getExecutiveInsightAction({ dimension, question }),
    summary: `${dimension}: ${question} (${value.toFixed(1)} pts).`,
    reading: value < 40
      ? 'Brecha severa dentro del levantamiento.'
      : 'Señal temprana que conviene monitorear.',
  };
};

const formatExecutiveInsightForNarrative = (item) => {
  if (typeof item === 'string') return item;
  if (!item) return '';
  const score = typeof item.value === 'number' ? item.value.toFixed(1) : item.value || '0';
  return item.summary || `${item.dimension || 'Dimensión'}: ${item.question || item.label || 'Señal'} (${score} pts).`;
};

const normalizeVerbatimText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const hashText = (value = '') => {
  let hash = 0;
  const input = String(value);
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const buildVerbatimId = ({ quote, naviera, company }) => (
  `vb_${hashText(`${normalizeVerbatimText(company)}|${normalizeVerbatimText(naviera)}|${normalizeVerbatimText(quote).toLowerCase()}`)}`
);

const buildVerbatimEntry = ({ quote, naviera, company }) => {
  const originalQuote = normalizeVerbatimText(quote);
  const resolvedCompany = company || 'Empresa participante';
  const resolvedNaviera = naviera || 'Sin dato';
  return {
    id: buildVerbatimId({ quote: originalQuote, naviera: resolvedNaviera, company: resolvedCompany }),
    company: resolvedCompany,
    naviera: resolvedNaviera,
    tone: analyzeSentiment(originalQuote),
    quote: originalQuote,
    originalQuote,
  };
};

const mergeCleanedVerbatims = (rawVerbatims = [], cleanedVerbatims = []) => {
  const byId = new Map((cleanedVerbatims || []).map((item) => [item.id, item]));
  const byOriginal = new Map((cleanedVerbatims || []).map((item) => [
    normalizeVerbatimText(item.originalQuote).toLowerCase(),
    item,
  ]));

  return rawVerbatims.map((item) => {
    const cleaned = byId.get(item.id) || byOriginal.get(normalizeVerbatimText(item.originalQuote).toLowerCase());
    const cleanedQuote = normalizeVerbatimText(cleaned?.cleanedQuote || cleaned?.keyIdea);
    const hasCleanedQuote = cleanedQuote && cleanedQuote.toLowerCase() !== normalizeVerbatimText(item.originalQuote).toLowerCase();

    return {
      ...item,
      quote: hasCleanedQuote ? cleanedQuote : item.originalQuote,
      cleanedQuote: hasCleanedQuote ? cleanedQuote : '',
      keyIdea: normalizeVerbatimText(cleaned?.keyIdea || ''),
      cleaningStatus: cleaned?.status || (hasCleanedQuote ? 'cleaned' : 'original'),
    };
  });
};

const getDiagnosticCarrierName = (detail = {}) => {
  const selected = detail.name?.toString().trim();
  if (!selected) return 'Naviera sin seleccionar';
  if (selected === 'Otra') return detail.otherName?.toString().trim() || 'Otra naviera';
  return selected;
};

const buildReportNarrativePayload = ({
  aggregate,
  period,
  previousPeriod,
  globalIndex,
  globalDelta,
  interpretation,
  participantCount,
  dimensionDrivers,
  dimensionDeltas,
  summaryInsights,
  sentimentDisplay,
  sectorLeaderboard,
  destinationLeaderboard,
  portLeaderboard,
  navieraStats,
  trendStats,
  segmentPressure,
  operationalFriction,
  qualitativeInsights,
  timeline,
  featuredVerbatims,
}) => {
  const submissions = aggregate?.sourceSubmissions || [];
  const negativeCommentsByNaviera = submissions.reduce((acc, submission) => {
    const diagnostics = getSubmissionNavieraDiagnostics(submission);
    diagnostics.forEach((detail) => {
      const naviera = getDiagnosticCarrierName(detail);
      if (!naviera) return;
      const comments = [
        detail.comment,
        submission.data?.meta_naviera_comentario,
        submission.data?.d1_q4,
        submission.data?.d2_q4,
      ]
        .map((value) => value?.toString().trim())
        .filter((value) => value && value.length >= 18 && analyzeSentiment(value) === 'negative');

      if (comments.length) {
        acc[naviera] = [...(acc[naviera] || []), ...comments].slice(0, 4);
      }
    });
    return acc;
  }, {});

  const navieras = navieraStats
    .map((item) => {
      const averageScore = typeof item.overallScore === 'number'
        ? item.overallScore
        : averageReportMetric([item.quality, item.agility, item.costs, item.availability]);
      return {
        name: item.name,
        sampleSize: item.sampleSize,
        evaluationCount: item.evaluationCount,
        confidenceLabel: item.confidenceLabel,
        averageScore: roundReportMetric(averageScore),
        complaintRisk: typeof item.complaintRisk === 'number'
          ? roundReportMetric(item.complaintRisk)
          : averageScore === null ? null : roundReportMetric(100 - averageScore),
        quality: roundReportMetric(item.quality),
        agility: roundReportMetric(item.agility),
        costs: roundReportMetric(item.costs),
        availability: roundReportMetric(item.availability),
        topProblems: item.topProblems || [],
        unresolvedCount: item.unresolvedCount || 0,
        negativeComments: Array.from(new Set([...(negativeCommentsByNaviera[item.name] || []), ...(item.comments || [])])).slice(0, 4),
      };
    })
    .sort((a, b) => (b.complaintRisk || 0) - (a.complaintRisk || 0) || (b.sampleSize || 0) - (a.sampleSize || 0));

  return {
    period,
    previousPeriod,
    participantCount,
    globalIndex: roundReportMetric(globalIndex),
    globalDelta: roundReportMetric(globalDelta),
    interpretation: interpretation?.text || '',
    trendHistory: timeline
      .filter((point) => typeof point.value === 'number')
      .map((point) => ({
        period: point.label,
        globalIndex: roundReportMetric(point.value),
      })),
    dimensions: dimensionDrivers.map((driver) => ({
      name: driver.name,
      score: roundReportMetric(driver.dimensionScore),
      delta: roundReportMetric(dimensionDeltas[driver.name]),
      bestSignal: driver.best ? {
        text: driver.best.text,
        value: roundReportMetric(driver.best.value),
      } : null,
      worstSignal: driver.worst ? {
        text: driver.worst.text,
        value: roundReportMetric(driver.worst.value),
      } : null,
    })),
    criticalFindings: (summaryInsights.critical || []).map(formatExecutiveInsightForNarrative).filter(Boolean),
    alerts: (summaryInsights.alerts || []).map(formatExecutiveInsightForNarrative).filter(Boolean),
    sentiment: sentimentDisplay ? {
      positivePct: sentimentDisplay.positivePct,
      neutralPct: sentimentDisplay.neutralPct,
      negativePct: sentimentDisplay.negativePct,
      sampleCount: sentimentDisplay.sampleCount || 0,
      netScore: sentimentDisplay.netScore,
      averageScore: sentimentDisplay.averageScore,
      confidenceLabel: sentimentDisplay.confidenceLabel,
      marginOfError: sentimentDisplay.marginOfError,
      intervals: sentimentDisplay.intervals,
    } : null,
    marketSignals: {
      priceTrend: trendStats[0]?.label || '',
      priceTrendMentions: trendStats[0]?.value || 0,
      topPort: portLeaderboard[0]?.label || '',
      topDestination: destinationLeaderboard[0]?.label || '',
    },
    segmentPressure: {
      sectors: (segmentPressure?.sectors || []).slice(0, 5).map((item) => ({
        label: item.label,
        count: item.count,
        score: item.score,
        pressure: item.pressure,
        weakestDimension: item.weakestDimension?.name || '',
        topProblem: item.topProblem?.label || '',
      })),
      destinations: (segmentPressure?.destinations || []).slice(0, 5).map((item) => ({
        label: item.label,
        count: item.count,
        score: item.score,
        pressure: item.pressure,
        weakestDimension: item.weakestDimension?.name || '',
        topProblem: item.topProblem?.label || '',
      })),
      ports: (segmentPressure?.ports || []).slice(0, 5).map((item) => ({
        label: item.label,
        count: item.count,
        score: item.score,
        pressure: item.pressure,
        weakestDimension: item.weakestDimension?.name || '',
        topProblem: item.topProblem?.label || '',
      })),
    },
    operationalFriction: operationalFriction ? {
      evaluationCount: operationalFriction.evaluationCount,
      incidentUnits: operationalFriction.incidentUnits,
      severeShare: operationalFriction.severeShare,
      unresolvedShare: operationalFriction.unresolvedShare,
      topProblems: operationalFriction.problemRows?.slice(0, 5),
      costImpacts: operationalFriction.costImpactRows?.slice(0, 4),
      impactDays: operationalFriction.impactDaysRows?.slice(0, 4),
      routes: operationalFriction.routeRows?.slice(0, 4),
      carriers: operationalFriction.carrierRows?.slice(0, 5),
    } : null,
    navieras,
    qualitativeThemes: qualitativeInsights.map((item) => ({
      theme: item.title,
      summary: item.summary,
      mentionCount: item.count,
      share: item.share,
      confidenceLabel: item.confidenceLabel,
      sourceQuestions: item.sourceQuestions,
      navieras: item.navieras,
      topWords: item.topWords,
      suggestedAction: item.action,
    })),
    sectors: sectorLeaderboard.map((item) => ({
      label: item.label,
      percent: item.percent,
      count: item.value,
    })),
    ports: portLeaderboard.map((item) => ({
      label: item.label,
      percent: item.percent,
      count: item.value,
    })),
    featuredVerbatims: (featuredVerbatims || []).slice(0, 8).map((item, index) => ({
      id: item.id || buildVerbatimId(item),
      label: `Exportador ${String(index + 1).padStart(2, '0')}`,
      naviera: item.naviera || 'Sin dato',
      tone: item.tone || analyzeSentiment(item.originalQuote || item.quote),
      originalQuote: item.originalQuote || item.quote,
    })),
  };
};

export const PublicDashboard = ({ history, enableAiNarrative = false, onNotify }) => {
  const dashboardRef = useRef(null);
  const periods = Object.keys(history).sort().reverse();
  const [selectedPeriod, setSelectedPeriod] = useState(periods[0] || '');
  const [selectedExportType] = useState('Todos');
  const [reportView, setReportView] = useState('executive');
  const [hoveredOtherSector, setHoveredOtherSector] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfAiNarrative, setPdfAiNarrative] = useState(null);
  const [pdfAiCleanedVerbatims, setPdfAiCleanedVerbatims] = useState([]);

  useEffect(() => {
    if (periods.length && !history[selectedPeriod]) {
      setSelectedPeriod(periods[0]);
    }
  }, [history, periods, selectedPeriod]);

  useEffect(() => {
    setPdfAiNarrative(null);
    setPdfAiCleanedVerbatims([]);
  }, [selectedPeriod]);

  const entry = history[selectedPeriod];
  const selectedPeriodIndex = periods.indexOf(selectedPeriod);
  const previousPeriod = selectedPeriodIndex >= 0 ? periods[selectedPeriodIndex + 1] : null;
  const previousEntry = previousPeriod ? history[previousPeriod] : null;

  const projectEntry = useCallback((targetEntry) => targetEntry || null, []);

  const aggregate = useMemo(() => projectEntry(entry), [entry, projectEntry]);
  const previousAggregate = useMemo(() => projectEntry(previousEntry), [previousEntry, projectEntry]);
  const cachedAiNarrative = aggregate?.aiNarrative || null;
  const cachedAiCleanedVerbatims = Array.isArray(aggregate?.aiCleanedVerbatims) ? aggregate.aiCleanedVerbatims : [];
  const activeAiCleanedVerbatims = pdfAiCleanedVerbatims.length ? pdfAiCleanedVerbatims : cachedAiCleanedVerbatims;
  const activeAiNarrative = pdfAiNarrative || cachedAiNarrative;

  const dimensionDrivers = useMemo(() => {
    if (!aggregate) return [];
    const { dimensionalIndices, questionAverages } = aggregate;
    return getDimensionDrivers(dimensionalIndices, questionAverages);
  }, [aggregate]);
  const sentimentSummary = useMemo(
    () => (aggregate
      ? summarizeSentiments(aggregate.sourceSubmissions)
      : {
          samples: { positive: [], negative: [], neutral: [] },
          positivePct: 0,
          negativePct: 0,
          neutralPct: 0,
          sampleCount: 0,
          confidenceLabel: 'sin muestra',
          marginOfError: 0,
        }),
    [aggregate],
  );
  const qualitativeInsights = useMemo(
    () => (aggregate ? buildQualitativeInsights(aggregate.sourceSubmissions).slice(0, 3) : []),
    [aggregate],
  );
  const sentimentDisplay = useMemo(() => {
    const totalReal = sentimentSummary.sampleCount || 0;
    return totalReal > 0 ? sentimentSummary : null;
  }, [sentimentSummary]);
  const sectorShares = useMemo(
    () => (aggregate ? aggregateShares(aggregate.sourceSubmissions, 'exportType') : []),
    [aggregate],
  );
  const otherSectorDetails = useMemo(() => {
    if (!aggregate?.sourceSubmissions?.length) return [];
    const details = aggregate.sourceSubmissions
      .filter((submission) => submission?.exportType?.toString().trim().toLowerCase() === 'otros')
      .map((submission) => submission?.otherExportTypeDetail?.toString().trim() || '')
      .filter(Boolean);
    return Array.from(new Set(details)).slice(0, 8);
  }, [aggregate]);
  const destinationShares = useMemo(
    () => (aggregate ? aggregateShares(aggregate.sourceSubmissions, 'destination') : []),
    [aggregate],
  );
  const trendStats = useMemo(() => (aggregate ? aggregateTrendShares(aggregate.sourceSubmissions) : []), [aggregate]);
  const portShares = useMemo(() => (aggregate ? aggregateShares(aggregate.sourceSubmissions, 'port') : []), [aggregate]);
  const executiveSummary = useMemo(
    () => (aggregate ? buildExecutiveSummary(aggregate.globalIndex, trendStats, sentimentDisplay) : ''),
    [aggregate, trendStats, sentimentDisplay],
  );
  const dimensionDeltas = useMemo(() => {
    if (!aggregate?.dimensionalIndices || !previousAggregate?.dimensionalIndices) return {};
    const deltas = {};
    Object.entries(aggregate.dimensionalIndices).forEach(([name, value]) => {
      const prev = previousAggregate.dimensionalIndices[name];
      if (typeof prev === 'number' && typeof value === 'number') {
        deltas[name] = value - prev;
      }
    });
    return deltas;
  }, [aggregate?.dimensionalIndices, previousAggregate?.dimensionalIndices]);
  const globalDelta = useMemo(() => {
    if (!aggregate || !previousAggregate) return null;
    return aggregate.globalIndex - previousAggregate.globalIndex;
  }, [aggregate, previousAggregate]);
  const formatDeltaMeta = (value) => {
    if (value === null || value === undefined) return null;
    const trendClass = value >= 0 ? 'text-emerald-600' : 'text-rose-600';
    const formatted = `${value >= 0 ? '+' : ''}${value.toFixed(1)} pts`;
    return { formatted, trendClass };
  };
  const perQuestionSummaries = useMemo(() => {
    if (!aggregate) return [];
    const dimensions = questionnaire.getDimensions();
    return dimensions.map((dimension) => ({
      id: dimension.id,
      name: dimension.name,
      weight: dimension.weight,
      questions: dimension.questions
        .filter((q) => q.type === 'likert' || q.type === 'binary')
        .map((q) => ({
          id: q.id,
          text: q.text,
          interpretation: q.interpretation,
          value: typeof aggregate.questionAverages?.[q.id] === 'number' ? aggregate.questionAverages[q.id] : null,
        })),
    }));
  }, [aggregate]);


  const laggingDimension = useMemo(() => {
    if (!dimensionDrivers.length) return null;
    return dimensionDrivers.reduce((worst, current) => {
      if (!worst || (current.dimensionScore ?? 0) < (worst.dimensionScore ?? 0)) return current;
      return worst;
    }, null);
  }, [dimensionDrivers]);

  const topTrend = trendStats[0];
  const participantCount = aggregate?.participantCount || 0;

  const dimPalette = ['#004AAD', '#0EA5E9', '#6366F1', '#14B8A6', '#F59E0B'];

  const sectorLeaderboard = useMemo(
    () =>
      sectorShares.map((item, index) => {
        const { icon: IconComp, accent, chip } = getSectorVisual(item.label);
        const percent = participantCount ? Math.round((item.value / participantCount) * 100) : 0;
        return {
          ...item,
          IconComp,
          accent,
          chip,
          percent,
          isLeader: index === 0,
        };
      }),
    [sectorShares, participantCount],
  );

  const destinationLeaderboard = useMemo(
    () =>
      destinationShares.map((item, index) => {
        const { icon: IconComp, accent, chip } = getDestinationVisual(item.label);
        const percent = participantCount ? Math.round((item.value / participantCount) * 100) : 0;
        return {
          ...item,
          IconComp,
          accent,
          chip,
          percent,
          isLeader: index === 0,
        };
      }),
    [destinationShares, participantCount],
  );

  const portLeaderboard = useMemo(
    () =>
      portShares.map((item, index) => {
        const { icon: IconComp, accent, chip } = getPortVisual(item.label);
        const percent = participantCount ? Math.round((item.value / participantCount) * 100) : 0;
        return {
          ...item,
          IconComp,
          accent,
          chip,
          percent,
          isLeader: index === 0,
        };
      }),
    [portShares, participantCount],
  );
  const segmentPressure = useMemo(() => {
    if (!aggregate?.sourceSubmissions?.length) return { sectors: [], destinations: [], ports: [] };
    return {
      sectors: buildSegmentPerformance(aggregate.sourceSubmissions, 'exportType').slice(0, 5),
      destinations: buildSegmentPerformance(aggregate.sourceSubmissions, 'destination').slice(0, 5),
      ports: buildSegmentPerformance(aggregate.sourceSubmissions, 'port').slice(0, 5),
    };
  }, [aggregate]);
  const operationalFriction = useMemo(
    () => (aggregate ? buildOperationalFriction(aggregate.sourceSubmissions || []) : null),
    [aggregate],
  );
  const hasOperationalFriction = Boolean(
    operationalFriction &&
    (
      operationalFriction.problemRows.length > 0 ||
      operationalFriction.incidentUnits > 0 ||
      operationalFriction.severeCount > 0 ||
      operationalFriction.unresolvedCount > 0
    ),
  );
  const segmentPressureCards = useMemo(() => ([
    { id: 'sector', label: 'Sector', item: segmentPressure.sectors[0], tone: 'blue' },
    { id: 'destination', label: 'Destino', item: segmentPressure.destinations[0], tone: 'teal' },
    { id: 'port', label: 'Puerto', item: segmentPressure.ports[0], tone: 'amber' },
  ].filter((card) => card.item)), [segmentPressure]);

  const portLeader = portLeaderboard[0];
  const PortLeaderIcon = portLeader?.IconComp || AnchorIcon;
  const summaryInsights = useMemo(() => {
    if (!aggregate) return { critical: [], alerts: [] };
    const critical = [];
    const alerts = [];
    const scoredQuestions = perQuestionSummaries
      .flatMap((dimension) => dimension.questions.map((q) => ({ ...q, dimension: dimension.name })))
      .filter((q) => typeof q.value === 'number');
    scoredQuestions.forEach((q) => {
      if (q.value < 40) {
        critical.push(buildExecutiveInsight(q, critical.length, 'critical'));
      } else if (q.value < 60) {
        alerts.push(buildExecutiveInsight(q, alerts.length, 'alert'));
      }
    });
    if (!critical.length && dimensionDrivers.length) {
      const weakestDimension = dimensionDrivers
        .slice()
        .sort((a, b) => (a.dimensionScore ?? 0) - (b.dimensionScore ?? 0))[0];
      if (weakestDimension && weakestDimension.dimensionScore !== undefined) {
        critical.push(buildExecutiveInsight({
          id: weakestDimension.id || weakestDimension.name,
          dimension: weakestDimension.name,
          text: 'Dimensión con menor desempeño del período',
          value: weakestDimension.dimensionScore || 0,
        }, 0, 'critical'));
      }
    }
    if (!alerts.length && laggingDimension) {
      alerts.push(buildExecutiveInsight({
        id: laggingDimension.id || laggingDimension.name,
        dimension: laggingDimension.name,
        text: 'Dimensión en riesgo para seguimiento',
        value: laggingDimension.dimensionScore || 0,
      }, 0, 'alert'));
    }
    return { critical: critical.slice(0, 4), alerts: alerts.slice(0, 4) };
  }, [aggregate, perQuestionSummaries, dimensionDrivers, laggingDimension]);
  const executiveInsightItems = useMemo(
    () => [...(summaryInsights.critical || []), ...(summaryInsights.alerts || [])].slice(0, 4),
    [summaryInsights],
  );
  const globalDeltaMeta = formatDeltaMeta(globalDelta);
  const netSentiment = sentimentDisplay ? sentimentDisplay.positivePct - sentimentDisplay.negativePct : null;
  const showExecutiveSummary =
    summaryInsights.critical.length > 0 ||
    summaryInsights.alerts.length > 0 ||
    Boolean(sentimentDisplay) ||
    Boolean(topTrend) ||
    Boolean(laggingDimension) ||
    Boolean(portLeader) ||
    segmentPressureCards.length > 0 ||
    hasOperationalFriction;
  // BEGIN OPTIONAL EXTENDED PDF PAGE MAP
  const executivePdfPageSources = useMemo(() => {
    if (!activeAiNarrative) return EXECUTIVE_PDF_PAGE_SOURCES;
    return [
      EXECUTIVE_PDF_PAGE_SOURCES[0],
      ['ai_narrative'],
      ...EXECUTIVE_PDF_PAGE_SOURCES.slice(1),
    ];
  }, [activeAiNarrative]);

  const extendedPdfPageSources = useMemo(() => {
    const questionPages = [];

    for (let index = 0; index < perQuestionSummaries.length; index += 2) {
      const pageSources = perQuestionSummaries
        .slice(index, index + 2)
        .map((dimension) => `questions-${dimension.id}`);

      if (index === 0) {
        questionPages.push(['extended-intro', ...pageSources]);
      } else {
        questionPages.push(pageSources);
      }
    }

    return [...executivePdfPageSources, ...questionPages];
  }, [executivePdfPageSources, perQuestionSummaries]);
  // END OPTIONAL EXTENDED PDF PAGE MAP
  const rawFeaturedVerbatims = useMemo(() => {
    return (aggregate?.sourceSubmissions || [])
      .flatMap((submission) => {
        const diagnosticQuotes = getSubmissionNavieraDiagnostics(submission)
          .map((detail) => ({
            quote: detail.comment?.toString().trim(),
            naviera: getDiagnosticCarrierName(detail),
          }))
          .filter((item) => item.quote && item.quote.length >= 24);
        const candidates = [submission?.data?.meta_naviera_comentario, submission?.data?.d1_q4, submission?.data?.d2_q4]
          .map((value) => value?.toString().trim())
          .filter((value) => value && value.length >= 24);

        return [
          ...diagnosticQuotes.map((item) => buildVerbatimEntry({
            company: submission.companyName || 'Empresa participante',
            naviera: item.naviera || submission.navieraPrincipal || 'Sin dato',
            quote: item.quote,
          })),
          ...candidates.map((quote) => buildVerbatimEntry({
            company: submission.companyName || 'Empresa participante',
            naviera: submission.navieraPrincipal || 'Sin dato',
            quote,
          })),
        ];
      })
      .filter((item, index, arr) => arr.findIndex((entry) => entry.id === item.id) === index)
      .slice(0, 4);
  }, [aggregate]);

  const featuredVerbatims = useMemo(
    () => mergeCleanedVerbatims(rawFeaturedVerbatims, activeAiCleanedVerbatims),
    [rawFeaturedVerbatims, activeAiCleanedVerbatims],
  );
  const verbatimToneStyles = {
    positive: { label: 'Positivo', badge: 'bg-emerald-50 text-emerald-700', strip: 'bg-emerald-400' },
    neutral:  { label: 'Neutral',  badge: 'bg-slate-100 text-slate-600',   strip: 'bg-slate-300'  },
    negative: { label: 'Alerta',   badge: 'bg-rose-50 text-rose-700',      strip: 'bg-rose-400'   },
  };
  const qualitativeToneStyles = {
    rose: { strip: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-100', bar: 'bg-rose-500' },
    amber: { strip: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-100', bar: 'bg-amber-500' },
    sky: { strip: 'bg-sky-500', badge: 'bg-sky-50 text-sky-700 border-sky-100', bar: 'bg-sky-500' },
    indigo: { strip: 'bg-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-100', bar: 'bg-indigo-500' },
    teal: { strip: 'bg-teal-500', badge: 'bg-teal-50 text-teal-700 border-teal-100', bar: 'bg-teal-500' },
    slate: { strip: 'bg-slate-400', badge: 'bg-slate-100 text-slate-600 border-slate-200', bar: 'bg-slate-400' },
  };
  const navieraStats = useMemo(() => {
    return buildNavieraPerformance(aggregate?.sourceSubmissions || []);
  }, [aggregate]);
  const navieraLeaders = useMemo(
    () => {
      if (!navieraStats.length) return [];
      return NAVIERA_METRIC_DESCRIPTORS.map((metric) => {
        const sorted = [...navieraStats].sort((a, b) => b[metric.key] - a[metric.key]);
        return {
          ...metric,
          leader: sorted[0],
          laggard: sorted[sorted.length - 1],
        };
      });
    },
    [navieraStats],
  );
  const navieraPerformanceRanking = useMemo(
    () => [...navieraStats].sort((a, b) => b.overallScore - a.overallScore || b.sampleSize - a.sampleSize).slice(0, 5),
    [navieraStats],
  );
  const navieraRiskRanking = useMemo(
    () => [...navieraStats].sort((a, b) => b.complaintRisk - a.complaintRisk || b.sampleSize - a.sampleSize).slice(0, 5),
    [navieraStats],
  );
  const timeline = useMemo(
    () =>
      periods
        .slice()
        .reverse()
        .map((periodKey) => ({
          label: periodKey,
          value: typeof history[periodKey]?.globalIndex === 'number' ? history[periodKey].globalIndex : null,
          dimensionalIndices: history[periodKey]?.dimensionalIndices || {},
        })),
    [history, periods],
  );
  const bestPeriodEntry = useMemo(() => {
    const withValues = timeline.filter((point) => typeof point.value === 'number');
    if (!withValues.length) return null;
    return withValues.reduce((best, current) => (current.value > best.value ? current : best));
  }, [timeline]);
  const lowestPeriodEntry = useMemo(() => {
    const withValues = timeline.filter((point) => typeof point.value === 'number');
    if (!withValues.length) return null;
    return withValues.reduce((lowest, current) => (current.value < lowest.value ? current : lowest));
  }, [timeline]);
  const strongestMomentum = useMemo(() => {
    const deltas = Object.entries(dimensionDeltas)
      .map(([name, delta]) => ({
        label: DIMENSION_LABEL_MAP[name] || name,
        delta,
      }))
      .filter((item) => typeof item.delta === 'number');
    if (!deltas.length) return null;
    return deltas.reduce((strongest, current) =>
      Math.abs(current.delta) > Math.abs(strongest.delta) ? current : strongest
    );
  }, [dimensionDeltas]);

  if (!aggregate) {
    return (
      <div className="p-10">
        <h1 className={`text-3xl font-bold ${themeColors.primary.text} mb-2`}>Dashboard de Resultados</h1>
        <p className="text-gray-600">No hay datos suficientes para mostrar este período.</p>
      </div>
    );
  }

  const { globalIndex, dimensionalIndices, period, questionAverages } = aggregate;
  const interpretation = methodology.getInterpretation(globalIndex);

  const handleExportCSV = () => {
    const rows = [
      ['Dimensión', 'Valor'],
      ...Object.entries(dimensionalIndices).map(([dim, value]) => [dim, value?.toFixed(2) ?? 'N/A']),
      [],
      ['Pregunta', 'Promedio normalizado'],
      ...Object.entries(questionAverages).map(([questionId, avg]) => {
        const question = questionnaire.getAllQuestions().find((q) => q.id === questionId);
        return [question?.text || questionId, avg?.toFixed(2) ?? 'N/A'];
      }),
    ];
    const csvContent = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `barometro_${period}_${selectedExportType}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getScoreBadge = (value) => {
    if (typeof value !== 'number') {
      return { label: 'Sin dato', chip: 'bg-slate-100 text-slate-500', bar: 'bg-slate-200' };
    }
    if (value >= 80) return { label: 'Excelente', chip: 'bg-emerald-100 text-emerald-700', bar: 'bg-emerald-400' };
    if (value >= 60) return { label: 'Estable', chip: 'bg-sky-100 text-sky-700', bar: 'bg-sky-400' };
    if (value >= 40) return { label: 'Alerta', chip: 'bg-amber-100 text-amber-700', bar: 'bg-amber-400' };
    return { label: 'Crítico', chip: 'bg-rose-100 text-rose-700', bar: 'bg-rose-400' };
  };

  const buildExtendedSourcesForExport = (hasAiNarrative) => {
    const baseSources = hasAiNarrative
      ? [EXECUTIVE_PDF_PAGE_SOURCES[0], ['ai_narrative'], ...EXECUTIVE_PDF_PAGE_SOURCES.slice(1)]
      : EXECUTIVE_PDF_PAGE_SOURCES;
    const questionPages = [];

    for (let index = 0; index < perQuestionSummaries.length; index += 2) {
      const pageSources = perQuestionSummaries
        .slice(index, index + 2)
        .map((dimension) => `questions-${dimension.id}`);

      if (index === 0) {
        questionPages.push(['extended-intro', ...pageSources]);
      } else {
        questionPages.push(pageSources);
      }
    }

    return [...baseSources, ...questionPages];
  };

  const generateAiArtifactsForExport = async () => {
    if (cachedAiNarrative && cachedAiCleanedVerbatims.length) {
      return { narrative: cachedAiNarrative, cleanedVerbatims: cachedAiCleanedVerbatims };
    }
    if (!enableAiNarrative || !firebaseEnabled) {
      return { narrative: cachedAiNarrative, cleanedVerbatims: cachedAiCleanedVerbatims };
    }

    try {
      const result = await generateFirebaseReportNarrative(
        buildReportNarrativePayload({
          aggregate,
          period,
          previousPeriod,
          globalIndex,
          globalDelta,
          interpretation,
          participantCount,
          dimensionDrivers,
          dimensionDeltas,
          summaryInsights,
          sentimentDisplay,
          sectorLeaderboard,
          destinationLeaderboard,
          portLeaderboard,
          navieraStats,
          trendStats,
          segmentPressure,
          operationalFriction,
          qualitativeInsights,
          timeline,
          featuredVerbatims: rawFeaturedVerbatims,
        }),
      );
      return {
        narrative: result?.narrative || cachedAiNarrative || null,
        cleanedVerbatims: Array.isArray(result?.cleanedVerbatims)
          ? result.cleanedVerbatims
          : cachedAiCleanedVerbatims,
      };
    } catch (error) {
      onNotify?.(error.message || 'No se pudo generar ni guardar la narrativa AI. El PDF se descargará sin esa sección.', 'error');
      return { narrative: cachedAiNarrative || null, cleanedVerbatims: cachedAiCleanedVerbatims };
    }
  };

  const handleExportPDF = async (mode = reportView) => {
    if (!dashboardRef.current) return;

    if (mode === 'executive') {
      setIsExportingPdf(true);
      setPdfAiNarrative(null);
      setPdfAiCleanedVerbatims([]);
      try {
        const aiArtifacts = await generateAiArtifactsForExport();
        const exportVerbatims = mergeCleanedVerbatims(rawFeaturedVerbatims, aiArtifacts.cleanedVerbatims);
        await generateExecutivePDF({
          period,
          globalIndex,
          interpretation,
          participantCount,
          dimensionDrivers,
          dimensionDeltas,
          summaryInsights,
          sentimentDisplay,
          sectorLeaderboard,
          destinationLeaderboard,
          portLeaderboard,
          featuredVerbatims: exportVerbatims,
          globalDeltaMeta,
          laggingDimension,
          portLeader,
          trendStats,
          executiveSummary,
          navieraStats,
          segmentPressure,
          operationalFriction,
          aiNarrative: aiArtifacts.narrative,
        });
      } finally {
        setIsExportingPdf(false);
      }
      return;
    }

    setIsExportingPdf(true);
    setPdfAiNarrative(null);
    setPdfAiCleanedVerbatims([]);
    const previousView = reportView;
    try {
      const aiArtifacts = await generateAiArtifactsForExport();
      const aiNarrative = aiArtifacts.narrative;
      setPdfAiNarrative(aiNarrative);
      setPdfAiCleanedVerbatims(aiArtifacts.cleanedVerbatims || []);
      if (mode !== reportView) {
        setReportView(mode);
      }
      await new Promise((resolve) => setTimeout(resolve, 0));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const [{ jsPDF }, html2canvasModule] = await Promise.all([import('jspdf'), import('html2canvas')]);
      const canvasTarget = dashboardRef.current;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const pageMargin = 6;
      const usablePdfWidth = pdfWidth - pageMargin * 2;
      const usablePdfHeight = pdfHeight - pageMargin * 2;
      let pageIndex = 0;

      const sourcePageMap =
        mode === 'executive'
          ? (aiNarrative ? [EXECUTIVE_PDF_PAGE_SOURCES[0], ['ai_narrative'], ...EXECUTIVE_PDF_PAGE_SOURCES.slice(1)] : EXECUTIVE_PDF_PAGE_SOURCES)
          : mode === 'extended'
            ? (aiNarrative ? buildExtendedSourcesForExport(true) : extendedPdfPageSources)
            : null;

      if (sourcePageMap) {
        const sourceNodes = Object.fromEntries(
          Array.from(canvasTarget.querySelectorAll('[data-pdf-source]')).map((node) => [
            node.getAttribute('data-pdf-source'),
            node,
          ]),
        );

        const pdfContentWidth = Math.min(canvasTarget.clientWidth, 1200);

        for (const pageSources of sourcePageMap) {
          const pageRoot = document.createElement('div');
          pageRoot.style.width = `${pdfContentWidth}px`;
          pageRoot.style.paddingTop = '20px';
          pageRoot.style.paddingBottom = '20px';
          pageRoot.style.paddingLeft = '0';
          pageRoot.style.paddingRight = '0';
          pageRoot.style.margin = '0';
          pageRoot.style.background = '#f8fafc';
          pageRoot.style.display = 'flex';
          pageRoot.style.flexDirection = 'column';
          pageRoot.style.gap = '20px';

          pageSources.forEach((sourceId) => {
            const sourceNode = sourceNodes[sourceId];
            if (sourceNode) {
              const clonedNode = sourceNode.cloneNode(true);
              copyCanvasElements(sourceNode, clonedNode);
              clonedNode.querySelectorAll('[data-pdf-hide]').forEach((el) => el.remove());
              clonedNode.querySelectorAll('[data-pdf-source]').forEach((nestedNode) => {
                const nestedSourceId = nestedNode.getAttribute('data-pdf-source');
                if (nestedSourceId && PDF_PAGE_SOURCE_BREAKS[nestedSourceId]) {
                  nestedNode.remove();
                }
              });
              pageRoot.appendChild(clonedNode);
            }
          });

          if (!pageRoot.childNodes.length) {
            continue;
          }

          const stagingHost = document.createElement('div');
          stagingHost.style.position = 'fixed';
          stagingHost.style.left = '-200vw';
          stagingHost.style.top = '0';
          stagingHost.style.zIndex = '-1';
          stagingHost.style.pointerEvents = 'none';
          stagingHost.appendChild(pageRoot);
          document.body.appendChild(stagingHost);

          try {
            const sectionCanvas = await html2canvasModule.default(pageRoot, {
              scale: 2,
              useCORS: true,
              scrollY: -window.scrollY,
              backgroundColor: '#f8fafc',
            });

            if (!sectionCanvas.width || !sectionCanvas.height) {
              continue;
            }

            const executiveScale = Math.min(
              usablePdfWidth / sectionCanvas.width,
              usablePdfHeight / sectionCanvas.height,
            );
            const renderedWidth = sectionCanvas.width * executiveScale;
            const renderedHeight = sectionCanvas.height * executiveScale;
            const offsetX = pageMargin + (usablePdfWidth - renderedWidth) / 2;
            const offsetY = pageMargin + (usablePdfHeight - renderedHeight) / 2;

            if (pageIndex > 0) {
              pdf.addPage();
            }

            pdf.addImage(
              sectionCanvas.toDataURL('image/png'),
              'PNG',
              offsetX,
              offsetY,
              renderedWidth,
              renderedHeight,
            );
            pageIndex += 1;
          } finally {
            document.body.removeChild(stagingHost);
          }
        }

        pdf.save(`barometro_${period}_${selectedExportType}_${mode}.pdf`);
        return;
      }

      const sections = Array.from(canvasTarget.querySelectorAll('[data-pdf-break="true"]'));

      for (const section of sections) {
        const sectionCanvas = await html2canvasModule.default(section, {
          scale: 2,
          useCORS: true,
          scrollY: -window.scrollY,
          backgroundColor: null,
        });

        if (!sectionCanvas.width || !sectionCanvas.height) {
          continue;
        }

        if (mode === 'executive') {
          const executiveScale = Math.min(
            usablePdfWidth / sectionCanvas.width,
            usablePdfHeight / sectionCanvas.height,
          );
          const renderedWidth = sectionCanvas.width * executiveScale;
          const renderedHeight = sectionCanvas.height * executiveScale;
          const offsetX = pageMargin + (usablePdfWidth - renderedWidth) / 2;
          const offsetY = pageMargin + (usablePdfHeight - renderedHeight) / 2;

          if (pageIndex > 0) {
            pdf.addPage();
          }

          pdf.addImage(
            sectionCanvas.toDataURL('image/png'),
            'PNG',
            offsetX,
            offsetY,
            renderedWidth,
            renderedHeight,
          );
          pageIndex += 1;
          continue;
        }

        const sectionRatio = usablePdfWidth / sectionCanvas.width;
        const sliceHeightPx = Math.max(1, Math.floor(usablePdfHeight / sectionRatio));
        let currentY = 0;

        while (currentY < sectionCanvas.height) {
          const remainingHeight = sectionCanvas.height - currentY;
          const currentSliceHeight = Math.min(sliceHeightPx, remainingHeight);
          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = sectionCanvas.width;
          pageCanvas.height = currentSliceHeight;
          const pageContext = pageCanvas.getContext('2d');

          if (!pageContext) {
            throw new Error('No se pudo preparar la página del PDF.');
          }

          pageContext.drawImage(
            sectionCanvas,
            0,
            currentY,
            sectionCanvas.width,
            currentSliceHeight,
            0,
            0,
            sectionCanvas.width,
            currentSliceHeight,
          );

          const pageImgData = pageCanvas.toDataURL('image/png');
          const renderedPageHeight = currentSliceHeight * sectionRatio;

          if (pageIndex > 0) {
            pdf.addPage();
          }

          pdf.addImage(pageImgData, 'PNG', pageMargin, pageMargin, usablePdfWidth, renderedPageHeight);
          currentY += currentSliceHeight;
          pageIndex += 1;
        }
      }

      pdf.save(`barometro_${period}_${selectedExportType}_${mode}.pdf`);
    } finally {
      if (mode !== previousView) {
        setReportView(previousView);
      }
      setPdfAiNarrative(null);
      setIsExportingPdf(false);
    }
  };

  return (
    <div className="p-4 md:p-8" ref={dashboardRef}>
      <div className="max-w-6xl mx-auto space-y-5">
        {/* BEGIN OPTIONAL EXECUTIVE PDF PAGE MAP */}
        <div data-pdf-page="executive" className="space-y-5">
        <div className="space-y-5" data-pdf-source="hero">
          <div className="card-premium p-5 md:p-6" data-pdf-break="true">
            <div className="flex flex-col gap-4">
              {/* Header row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <LogoBlue className="h-7 w-auto flex-shrink-0" />
                  <div className="w-px h-6 bg-slate-200 hidden sm:block" />
                  <div>
                    <p className="text-[0.58rem] uppercase text-slate-400 leading-none mb-0.5">Barómetro Naviero</p>
                    <h1 className="text-lg font-semibold text-slate-900 leading-tight">Briefing Ejecutivo</h1>
                  </div>
                </div>
                <p className="text-sm text-slate-400">Período <span className="font-semibold text-slate-700">{period}</span></p>
              </div>
              {/* Controls row */}
              <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
                <label className="flex flex-col gap-1 text-[0.58rem] font-semibold uppercase text-slate-400">
                  Período
                  <div className="relative">
                    <select
                      value={selectedPeriod}
                      onChange={(e) => setSelectedPeriod(e.target.value)}
                      className="appearance-none rounded-xl bg-slate-50 border border-slate-200 pl-3.5 pr-8 py-2 text-sm font-medium text-slate-700 focus:outline-none focus:border-[#004AAD] focus:ring-2 focus:ring-[#004AAD]/10 transition-colors"
                    >
                      {periods.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-slate-400 text-xs">▼</span>
                  </div>
                </label>
                <div className="flex flex-col gap-1 text-[0.58rem] font-semibold uppercase text-slate-400">
                  Vista
                  <div className="inline-flex rounded-xl bg-slate-100 p-0.5">
                    {REPORT_VIEWS.map((option) => {
                      const isActive = reportView === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setReportView(option.id)}
                          className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                            isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="sm:ml-auto flex flex-wrap gap-2">
                  <button
                    onClick={handleExportCSV}
                    className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExportPDF('executive')}
                    disabled={isExportingPdf}
                    className="px-3.5 py-2 rounded-xl text-white text-xs font-semibold bg-[#004AAD] hover:bg-[#003B8A] transition-colors shadow-sm disabled:cursor-wait disabled:opacity-70"
                  >
                    {isExportingPdf ? 'Generando PDF' : 'PDF Ejecutivo'}
                  </button>
                  <button
                    onClick={() => handleExportPDF('extended')}
                    disabled={isExportingPdf}
                    className="px-3.5 py-2 rounded-xl text-white text-xs font-semibold bg-[#152744] hover:bg-[#0f1e35] transition-colors shadow-sm disabled:cursor-wait disabled:opacity-70"
                  >
                    {isExportingPdf ? 'Generando PDF' : 'PDF Extendido'}
                  </button>
                </div>
              </div>
              <div className="grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-[0.58rem] uppercase text-slate-400">Índice global</p>
                  <div className="mt-1 flex items-end gap-2">
                    <p className="text-4xl font-extrabold font-jakarta leading-none text-[#004AAD]">{globalIndex.toFixed(1)}</p>
                    <p className="pb-1 text-sm font-semibold text-slate-700">{interpretation.text}</p>
                  </div>
                </div>
                <div className="sm:border-l sm:border-slate-100 sm:pl-4">
                  <p className="text-[0.58rem] uppercase text-slate-400">Participantes</p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">{participantCount}</p>
                  <p className="text-xs text-slate-500">empresas aprobadas</p>
                </div>
                <div className="sm:border-l sm:border-slate-100 sm:pl-4">
                  <p className="text-[0.58rem] uppercase text-slate-400">Foco crítico</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{laggingDimension?.name || 'Sin brechas'}</p>
                  <p className="text-xs text-rose-500">{laggingDimension ? `${laggingDimension.dimensionScore?.toFixed(1)} pts` : 'Sin alertas activas'}</p>
                </div>
                <div className="sm:border-l sm:border-slate-100 sm:pl-4">
                  <p className="text-[0.58rem] uppercase text-slate-400">Señal de mercado</p>
                  <p className="mt-1 text-lg font-semibold text-slate-900">{topTrend?.label || 'Por levantar'}</p>
                  <p className="text-xs text-slate-500">{topTrend ? `${topTrend.value} menciones` : 'Sin menciones válidas'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showExecutiveSummary && (
        <div className="card-premium p-6 space-y-4" data-pdf-break="true" data-pdf-source="summary">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase text-slate-400">Resumen ejecutivo</p>
                <p className="text-lg font-semibold text-slate-900">Lo más relevante del período</p>
              </div>
              {globalDeltaMeta && (
                <span className={`text-sm font-semibold ${globalDeltaMeta.trendClass}`}>
                  {globalDeltaMeta.formatted} vs {previousPeriod || 'mes anterior'}
                </span>
              )}
            </div>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start">
              <div className="rounded-xl border border-slate-200/70 bg-slate-50/70 p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[0.65rem] uppercase text-slate-400">Prioridades accionables</p>
                    <p className="text-sm font-semibold text-slate-800">Ranking de señales que explican la presión del período</p>
                  </div>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-500">
                    {executiveInsightItems.length} focos
                  </span>
                </div>
                {executiveInsightItems.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {executiveInsightItems.map((item, index) => {
                      const width = Math.min(100, Math.max(0, item.value || 0));
                      return (
                        <article key={item.id} className={`rounded-xl border p-4 shadow-sm ${item.tone.frame}`}>
                          <div className="mb-3 flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white ${item.tone.accent}`}>
                                {index + 1}
                              </span>
                              <div>
                                <p className="text-[0.6rem] uppercase text-slate-400">{item.dimension}</p>
                                <p className={`text-xs font-bold uppercase ${item.tone.text}`}>{item.severity}</p>
                              </div>
                            </div>
                            <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${item.tone.pill}`}>
                              {item.value.toFixed(1)}
                            </span>
                          </div>
                          <p className="min-h-[2.75rem] text-sm font-semibold leading-snug text-slate-900">{item.question}</p>
                          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
                            <div className={`h-full ${item.tone.bar}`} style={{ width: `${width}%` }} />
                          </div>
                          <div className="mt-3 rounded-lg bg-white/75 p-3">
                            <p className="text-[0.58rem] uppercase text-slate-400">Lectura</p>
                            <p className="text-xs font-medium leading-snug text-slate-600">{item.reading}</p>
                          </div>
                          <p className="mt-3 text-xs leading-snug text-slate-600">
                            <span className="font-semibold text-slate-800">Acción:</span> {item.action}
                          </p>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
                    <p className="text-sm font-semibold text-emerald-700">Sin brechas críticas en este período.</p>
                    <p className="mt-1 text-sm text-emerald-700/80">Mantener monitoreo por naviera, destino y puerto para detectar deterioros tempranos.</p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {sentimentDisplay && (
                  <div className="rounded-xl border border-slate-200/70 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[0.65rem] uppercase text-slate-400">Sentimiento de verbatims</p>
                        <p className="text-sm text-slate-600">Pulso emocional de los comentarios</p>
                      </div>
                      <span className="text-xs text-slate-400">
                        {sentimentDisplay.sampleCount} comentarios · {sentimentDisplay.confidenceLabel}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                        <span className="bg-emerald-500" style={{ width: `${sentimentDisplay.positivePct}%` }} />
                        <span className="bg-slate-400" style={{ width: `${sentimentDisplay.neutralPct}%` }} />
                        <span className="bg-rose-500" style={{ width: `${sentimentDisplay.negativePct}%` }} />
                      </div>
                      <div className="flex justify-between text-[0.65rem] uppercase text-slate-500">
                        <span className="text-emerald-600">Positivo {sentimentDisplay.positivePct}%</span>
                        <span>Neutral {sentimentDisplay.neutralPct}%</span>
                        <span className="text-rose-600">Negativo {sentimentDisplay.negativePct}%</span>
                      </div>
                      {netSentiment !== null && (
                        <p className="text-xs text-slate-500">
                          Sentimiento neto:{' '}
                          <span className={netSentiment >= 0 ? 'text-emerald-600 font-semibold' : 'text-rose-600 font-semibold'}>
                            {netSentiment >= 0 ? '+' : ''}
                            {netSentiment.toFixed(1)} pts
                          </span>
                          {sentimentDisplay.marginOfError > 0 && (
                            <span className="ml-1 text-slate-400">
                              · margen aprox. ±{sentimentDisplay.marginOfError} pts
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {(topTrend || laggingDimension || portLeader) && (
                  <div className="rounded-xl border border-slate-200/70 p-4 space-y-4">
                    <div>
                      <p className="text-[0.65rem] uppercase text-slate-400">Alertas operativas</p>
                      <p className="text-sm text-slate-600">Monitorea los focos críticos</p>
                    </div>
                    <div className="space-y-3">
                      {topTrend && (
                        <div className="flex items-center gap-3 border border-slate-200/70 rounded-xl p-3">
                          <span className="p-2 rounded-xl bg-amber-50 text-amber-600">
                            <TrendingUpIcon className="h-5 w-5" />
                          </span>
                          <div>
                            <p className="text-xs uppercase text-slate-400">Variación de precios</p>
                            <p className="text-base font-semibold text-slate-900">{topTrend.label}</p>
                            <p className="text-sm text-slate-500">{`${topTrend.value} menciones`}</p>
                          </div>
                        </div>
                      )}
                      {laggingDimension && (
                        <div className="flex items-center gap-3 border border-slate-200/70 rounded-xl p-3">
                          <span className="p-2 rounded-xl bg-rose-50 text-rose-600">
                            <AlertIcon className="h-5 w-5" />
                          </span>
                          <div>
                            <p className="text-xs uppercase text-slate-400">Dimensión en riesgo</p>
                            <p className="text-base font-semibold text-slate-900">{laggingDimension.name}</p>
                            <p className="text-sm text-rose-500">{`${laggingDimension.dimensionScore?.toFixed(1)} pts`}</p>
                          </div>
                        </div>
                      )}
                      {portLeader && (
                        <div className="flex items-center gap-3 border border-slate-200/70 rounded-xl p-3">
                          <span className="p-2 rounded-xl bg-slate-50 text-slate-600">
                            <PortLeaderIcon className="h-5 w-5" />
                          </span>
                          <div>
                            <p className="text-xs uppercase text-slate-400">Puerto con mayor flujo</p>
                            <p className="text-base font-semibold text-slate-900">{portLeader.label}</p>
                            <p className="text-sm text-slate-500">{`${portLeader.value} empresas (${portLeader.percent}%)`}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>

        {activeAiNarrative && (
          <AiNarrativeReportSection narrative={activeAiNarrative} />
        )}

        {/* BEGIN OPTIONAL TREND SECTION */}
        <div data-pdf-page="executive" className="space-y-5">
        {timeline.filter((p) => typeof p.value === 'number').length > 1 && (
          <TrendSection
            selectedPeriod={selectedPeriod}
            timeline={timeline}
            globalDeltaMeta={globalDeltaMeta}
            bestPeriodEntry={bestPeriodEntry}
            lowestPeriodEntry={lowestPeriodEntry}
            strongestMomentum={strongestMomentum}
            dataPdfSource="trend"
          />
        )}
        {/* END OPTIONAL TREND SECTION */}

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5" data-pdf-break="true" data-pdf-source="indices">
          {/* Global Index Card */}
          <div className="bg-hero-gradient rounded-2xl text-white p-6 shadow-card flex flex-col gap-4">
            <div>
              <p className="text-[0.6rem] uppercase text-white/55">Índice Global · {period}</p>
              <div className="flex items-end gap-3 mt-1">
                <p className="text-5xl font-extrabold font-jakarta leading-none">{globalIndex.toFixed(1)}</p>
                <div className="pb-1 space-y-0.5">
                  <p className="text-sm font-semibold text-white/90">{interpretation.text}</p>
                  {globalDeltaMeta && (
                    <p className={`text-xs font-semibold ${globalDeltaMeta.trendClass}`}>
                      {globalDeltaMeta.formatted} vs {previousPeriod || 'período anterior'}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <ScoreArcGauge value={globalIndex} />
            <p className="text-xs text-white/70 leading-relaxed border-t border-white/[0.08] pt-3">{executiveSummary}</p>
            <div className="grid grid-cols-2 gap-3 border-t border-white/[0.08] pt-3">
              <div>
                <p className="text-[0.6rem] uppercase text-white/50">Participantes</p>
                <p className="text-xl font-bold font-jakarta">{participantCount}</p>
              </div>
              <div>
                <p className="text-[0.6rem] uppercase text-white/50">Período</p>
                <p className="text-xl font-bold font-jakarta">{period}</p>
              </div>
            </div>
          </div>
          {/* Dimension Score List */}
          <div className="card-premium p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
              <div>
                <p className="text-[0.6rem] uppercase text-slate-400">Pilares del índice</p>
                <p className="text-sm font-semibold text-slate-800">Desempeño por dimensión</p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {dimensionDrivers.map((driver, idx) => {
                const label = DIMENSION_LABEL_MAP[driver.name] || driver.name.split(' ')[0];
                const score = driver.dimensionScore ?? 0;
                const delta = dimensionDeltas[driver.name];
                const color = dimPalette[idx % dimPalette.length];
                const badge = score >= 75 ? { label: 'Excelente', bg: 'bg-emerald-50 text-emerald-700' } :
                              score >= 50 ? { label: 'Estable', bg: 'bg-sky-50 text-sky-700' } :
                              score >= 25 ? { label: 'Alerta', bg: 'bg-amber-50 text-amber-700' } :
                              { label: 'Crítico', bg: 'bg-rose-50 text-rose-700' };
                return (
                  <div key={driver.id} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-sm font-medium text-slate-700">{label}</span>
                        <span className={`text-[0.6rem] font-semibold px-2 py-0.5 rounded-full ${badge.bg}`}>{badge.label}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {delta !== undefined && (
                          <span className={`text-[0.65rem] font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {delta >= 0 ? '+' : ''}{delta.toFixed(1)}
                          </span>
                        )}
                        <span className="text-sm font-bold font-jakarta text-slate-900 w-10 text-right">{score.toFixed(1)}</span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>

        <div data-pdf-page="executive" className="space-y-5">
        <div className="grid grid-cols-1 gap-6" data-pdf-break="true" data-pdf-source="navieras">
          <div className="card-premium p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Navieras evaluadas</p>
                  <p className="text-sm font-semibold text-slate-800">Índice ejecutivo por factor</p>
                </div>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">{navieraStats.length} compañías</span>
            </div>
            {navieraLeaders.length > 0 ? (
              <>
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-slate-400">Regla de lectura</p>
                    <p className="text-xs text-slate-600">El color representa la categoría del score. Verde es mejor desempeño; naranja/rojo indica mayor fricción.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5 lg:min-w-[520px]">
                    {SCORE_BANDS.map((band) => (
                      <div key={band.label} className={`rounded-lg border px-2 py-1 ${band.bg} ${band.border}`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`h-2 w-2 rounded-full ${band.dot}`} />
                          <span className={`text-[0.6rem] font-bold uppercase ${band.text}`}>{band.label}</span>
                        </div>
                        <p className="mt-0.5 text-[0.58rem] text-slate-500">{band.range}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {navieraLeaders.map((metric) => {
                  const ranking = [...navieraStats]
                    .sort((a, b) => b[metric.key] - a[metric.key] || b.sampleSize - a.sampleSize || a.name.localeCompare(b.name))
                    .slice(0, 5);
                  const categoryCounts = SCORE_BANDS
                    .map((band) => ({
                      ...band,
                      count: ranking.filter((item) => getScoreBand(item[metric.key]).label === band.label).length,
                    }))
                    .filter((band) => band.count > 0);

                  return (
                  <div key={metric.key} className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-400">{metric.label}</p>
                        <p className="mt-0.5 text-sm font-bold text-slate-800">{metric.description}</p>
                      </div>
                      <span className="rounded-lg bg-slate-50 px-2 py-1 text-[0.6rem] font-semibold uppercase text-slate-400">score /100</span>
                    </div>

                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {categoryCounts.map((band) => (
                        <span key={band.label} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6rem] font-semibold ${band.bg} ${band.border} ${band.text}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${band.dot}`} />
                          {band.count} {band.label.toLowerCase()}
                        </span>
                      ))}
                    </div>

                    <div className="space-y-2">
                      {ranking.map((item, index) => {
                        const score = clampScoreValue(item[metric.key]);
                        const band = getScoreBand(score);
                        return (
                          <div key={`${metric.key}_${item.name}`} className={`rounded-xl border px-3 py-2 ${band.bg} ${band.border}`}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${band.dot}`} />
                                <p className="truncate text-sm font-bold text-slate-900">#{index + 1} {item.name}</p>
                                <span className={`hidden rounded-full bg-white/75 px-2 py-0.5 text-[0.58rem] font-bold uppercase sm:inline ${band.text}`}>
                                  {band.label}
                                </span>
                              </div>
                              <span className={`text-base font-black font-jakarta ${band.text}`}>{score.toFixed(1)}</span>
                            </div>
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/80">
                              <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${score}%` }} />
                            </div>
                            <div className="mt-1.5 flex items-center justify-between gap-2 text-[0.62rem] text-slate-500">
                              <span>{item.highlight}</span>
                              <span className={`flex-shrink-0 font-semibold ${band.text}`}>{band.helper}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500">
                No hay suficientes respuestas reales para construir el comparativo de navieras en este período.
              </div>
            )}
            {navieraStats.length > 0 && (
              <div className="grid gap-4 border-t border-slate-100 pt-5 lg:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-400">Desempeño consolidado</p>
                      <p className="text-sm font-semibold text-slate-800">Ranking global por naviera</p>
                    </div>
                    <span className="rounded-lg bg-slate-50 px-2 py-1 text-[0.6rem] font-semibold uppercase text-slate-500">
                      score
                    </span>
                  </div>
                  <div className="space-y-2">
                    {navieraPerformanceRanking.map((item, index) => {
                      const score = clampScoreValue(item.overallScore);
                      const band = getScoreBand(score);
                      return (
                      <div key={item.name} className={`rounded-xl border px-3 py-2 ${band.bg} ${band.border}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-bold text-slate-900">#{index + 1} {item.name}</p>
                              <span className={`rounded-full bg-white/75 px-2 py-0.5 text-[0.58rem] font-bold uppercase ${band.text}`}>{band.label}</span>
                            </div>
                            <p className="mt-0.5 text-[0.65rem] text-slate-500">{item.highlight}</p>
                          </div>
                          <p className={`text-lg font-black font-jakarta ${band.text}`}>{score.toFixed(1)}</p>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/80">
                          <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${score}%` }} />
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-400">Fricción operativa</p>
                      <p className="text-sm font-semibold text-slate-800">Riesgo de queja e impacto</p>
                    </div>
                    <span className="rounded-lg bg-slate-50 px-2 py-1 text-[0.6rem] font-semibold uppercase text-slate-500">
                      riesgo
                    </span>
                  </div>
                  <div className="space-y-2">
                    {navieraRiskRanking.map((item, index) => {
                      const topProblem = item.topProblems?.[0];
                      const risk = clampScoreValue(item.complaintRisk);
                      const band = getRiskBand(risk);
                      return (
                        <div key={item.name} className={`rounded-xl border px-3 py-2 ${band.bg} ${band.border}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-bold text-slate-900">#{index + 1} {item.name}</p>
                                <span className={`rounded-full bg-white/75 px-2 py-0.5 text-[0.58rem] font-bold uppercase ${band.text}`}>{band.label}</span>
                              </div>
                              <p className="text-[0.65rem] text-slate-500">
                                {topProblem ? `${topProblem.label} · ${topProblem.count} mención${topProblem.count === 1 ? '' : 'es'}` : item.highlight}
                              </p>
                            </div>
                            <p className={`text-lg font-black font-jakarta ${band.text}`}>{risk.toFixed(1)}</p>
                          </div>
                          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/80">
                            <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${risk}%` }} />
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {(item.topProblems || []).slice(0, 3).map((problem) => (
                              <span key={problem.label} className="rounded-full bg-rose-50 px-2 py-0.5 text-[0.6rem] font-semibold text-rose-600">
                                {problem.label}
                              </span>
                            ))}
                            {item.unresolvedCount > 0 && (
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[0.6rem] font-semibold text-amber-700">
                                {item.unresolvedCount} sin resolver
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {sectorLeaderboard.length > 0 && (
          <div className="card-premium p-6 space-y-4" data-pdf-break="true" data-pdf-source="sectors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Ranking de sectores</p>
                  <p className="text-sm font-semibold text-slate-800">Participación por industria</p>
                </div>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">{sectorLeaderboard.length} sectores</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sectorLeaderboard.map((sector, rankIdx) => {
                const IconComp = sector.IconComp;
                const percentWidth = Math.min(100, Math.max(0, sector.percent || 0));
                const isOtherSectorCard = sector.label?.toString().trim().toLowerCase() === 'otros';
                const canShowOthersPopover = ENABLE_OTHERS_SECTOR_HOVER_DETAILS && isOtherSectorCard;
                const rankLabel = `#${String(rankIdx + 1).padStart(2, '0')}`;
                return (
                  <div
                    key={sector.label}
                    className={`relative overflow-visible flex items-center gap-3 rounded-xl border px-3 py-3 ${
                      sector.isLeader ? 'border-[#004AAD]/20 bg-[#004AAD]/5 shadow-sm' : 'border-slate-200/70 bg-white'
                    }`}
                    onMouseEnter={canShowOthersPopover ? () => setHoveredOtherSector(true) : undefined}
                    onMouseLeave={canShowOthersPopover ? () => setHoveredOtherSector(false) : undefined}
                  >
                    {canShowOthersPopover && hoveredOtherSector && !isExportingPdf && (
                      <div className="pointer-events-none absolute left-3 right-3 -top-2 -translate-y-full rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm z-20">
                        <p className="text-[0.6rem] uppercase text-slate-400">Detalle de Otros</p>
                        <p className="text-xs text-slate-600 mt-1">
                          {otherSectorDetails.length > 0 ? otherSectorDetails.join(', ') : 'Sin detalle registrado en este período.'}
                        </p>
                      </div>
                    )}
                    <p className={`text-[0.6rem] font-bold tabular-nums flex-shrink-0 w-7 text-center ${sector.isLeader ? 'text-[#004AAD]' : 'text-slate-300'}`}>{rankLabel}</p>
                    <span className={`p-2 rounded-xl flex-shrink-0 ${sector.accent}`}>
                      <IconComp className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800 truncate">{sector.label}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-sm font-bold text-slate-700">{sector.value}</span>
                          <span className="text-[0.6rem] text-slate-400 uppercase">env.</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="h-1.5 bg-slate-100 rounded-full flex-1 overflow-hidden">
                          <div
                            className={`h-full transition-all ${sector.isLeader ? 'bg-[#004AAD]' : 'bg-slate-300'}`}
                            style={{ width: `${percentWidth}%` }}
                          />
                        </div>
                        <span className="text-[0.65rem] font-semibold text-slate-500 flex-shrink-0">{sector.percent}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(segmentPressureCards.length > 0 || hasOperationalFriction) && (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-5" data-pdf-break="true" data-pdf-source="segments">
            {segmentPressureCards.length > 0 && (
              <div className="card-premium p-6 space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                    <div>
                      <p className="text-[0.6rem] uppercase text-slate-400">Presión por segmento</p>
                      <p className="text-sm font-semibold text-slate-800">Dónde se concentra la menor experiencia</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
                    {segmentPressureCards.length} cortes
                  </span>
                </div>
                <div className="grid gap-3">
                  {segmentPressureCards.map((card) => {
                    const item = card.item;
                    const band = getScoreBand(item.score);
                    const tone = SEGMENT_TONE_STYLES[card.tone] || SEGMENT_TONE_STYLES.blue;
                    const weakLabel = item.weakestDimension?.name
                      ? (DIMENSION_LABEL_MAP[item.weakestDimension.name] || item.weakestDimension.name)
                      : 'Sin brecha dominante';
                    return (
                      <article key={card.id} className={`rounded-xl border p-4 ${tone.soft} ${tone.border}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="text-[0.6rem] uppercase text-slate-400">{card.label}</p>
                            <h3 className="mt-0.5 truncate text-lg font-bold text-slate-900">{item.label}</h3>
                            <p className="mt-1 text-xs text-slate-500">
                              {item.count} empresa{item.count === 1 ? '' : 's'} · {item.confidenceLabel}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-black font-jakarta ${band.text}`}>{item.score.toFixed(1)}</p>
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[0.58rem] font-bold uppercase ${band.bg} ${band.border} ${band.text}`}>
                              {band.label}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/80">
                          <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${Math.max(4, Math.min(100, item.score))}%` }} />
                        </div>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          <div className="rounded-lg bg-white/75 p-3">
                            <p className="text-[0.58rem] uppercase text-slate-400">Brecha</p>
                            <p className="mt-1 text-xs font-semibold text-slate-700">{weakLabel}</p>
                          </div>
                          <div className="rounded-lg bg-white/75 p-3">
                            <p className="text-[0.58rem] uppercase text-slate-400">Fricción</p>
                            <p className="mt-1 text-xs font-semibold text-slate-700">
                              {item.topProblem?.label || item.topRoute?.label || 'Sin fricción dominante'}
                            </p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            )}

            {hasOperationalFriction && (
              <div className="card-premium p-6 space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-1 h-5 rounded-full bg-rose-500 flex-shrink-0" />
                    <div>
                      <p className="text-[0.6rem] uppercase text-slate-400">Incidentes e impacto</p>
                      <p className="text-sm font-semibold text-slate-800">Severidad, resolución y costos declarados</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
                    {operationalFriction.evaluationCount} eval.
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <p className="text-[0.58rem] uppercase text-slate-400">Incidentes</p>
                    <p className="mt-1 text-xl font-black text-slate-900">{formatMetricValue(operationalFriction.incidentUnits)}</p>
                    <p className="text-[0.62rem] text-slate-500">aprox.</p>
                  </div>
                  <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-3">
                    <p className="text-[0.58rem] uppercase text-orange-500">Alta severidad</p>
                    <p className="mt-1 text-xl font-black text-orange-700">{operationalFriction.severeShare}%</p>
                    <p className="text-[0.62rem] text-orange-700/70">{operationalFriction.severeCount} casos</p>
                  </div>
                  <div className="rounded-xl border border-rose-100 bg-rose-50/70 p-3">
                    <p className="text-[0.58rem] uppercase text-rose-500">Sin cierre total</p>
                    <p className="mt-1 text-xl font-black text-rose-700">{operationalFriction.unresolvedShare}%</p>
                    <p className="text-[0.62rem] text-rose-700/70">{operationalFriction.unresolvedCount} casos</p>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-3">
                    <p className="text-[0.65rem] uppercase text-slate-400">Problemas dominantes</p>
                    {(operationalFriction.problemRows.length ? operationalFriction.problemRows : operationalFriction.routeRows).slice(0, 4).map((row) => (
                      <div key={row.label} className="rounded-xl border border-slate-200/70 bg-white px-3 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="truncate text-sm font-semibold text-slate-800">{row.label}</p>
                          <span className="text-xs font-bold text-slate-500">{row.value}</span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-rose-500" style={{ width: `${Math.max(6, row.percent)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3">
                    <p className="text-[0.65rem] uppercase text-slate-400">Navieras a monitorear</p>
                    {operationalFriction.carrierRows.slice(0, 4).map((row) => {
                      const band = getRiskBand(row.risk);
                      return (
                        <div key={row.name} className={`rounded-xl border px-3 py-2 ${band.bg} ${band.border}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-slate-900">{row.name}</p>
                              <p className="text-[0.62rem] text-slate-500">
                                {row.topProblems[0]?.label || row.topRoute?.label || `${row.evaluationCount} evaluaciones`}
                              </p>
                            </div>
                            <p className={`text-base font-black font-jakarta ${band.text}`}>{row.risk.toFixed(1)}</p>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {row.unresolvedCount > 0 && (
                              <span className="rounded-full bg-white/75 px-2 py-0.5 text-[0.58rem] font-bold text-rose-600">
                                {row.unresolvedCount} sin cierre
                              </span>
                            )}
                            {row.severeCount > 0 && (
                              <span className="rounded-full bg-white/75 px-2 py-0.5 text-[0.58rem] font-bold text-orange-600">
                                {row.severeCount} severos
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {operationalFriction.costImpactRows.some((row) => row.label !== 'Sin dato') && (
                  <div className="rounded-xl border border-slate-200/70 bg-slate-50/70 p-3">
                    <p className="text-[0.58rem] uppercase text-slate-400">Impacto económico más reportado</p>
                    <p className="mt-1 text-sm font-semibold text-slate-800">
                      {operationalFriction.costImpactRows.find((row) => row.label !== 'Sin dato')?.label || 'Sin dato'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>

        <div data-pdf-page="executive" className="space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-pdf-break="true" data-pdf-source="drivers">
          {dimensionDrivers.map((driver, idx) => {
            const color = dimPalette[idx % dimPalette.length];
            const score = driver.dimensionScore ?? 0;
            const delta = dimensionDeltas[driver.name];
            return (
            <div key={driver.id} className="card-premium card-premium-hover overflow-hidden flex flex-col">
              <div className="h-[3px]" style={{ backgroundColor: color }} />
              <div className="p-5 space-y-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.6rem] uppercase text-slate-400 leading-none mb-1">{DIMENSION_LABEL_MAP[driver.name] || driver.name}</p>
                    <p className="text-3xl font-extrabold font-jakarta text-slate-900 leading-none">{score.toFixed(1)}</p>
                    {delta !== undefined && (
                      <p className={`text-xs font-semibold mt-1 ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)} pts
                      </p>
                    )}
                  </div>
                  <MiniScoreRing value={score} color={color} />
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
                </div>
                {driver.best && (
                  <div className="rounded-xl p-3 space-y-1" style={{ backgroundColor: `${color}0D` }}>
                    <p className="text-[0.6rem] uppercase" style={{ color }}>Impulsor clave</p>
                    <p className="text-xs text-slate-700 font-medium leading-snug">{driver.best.text}</p>
                    <p className="text-xs font-bold" style={{ color }}>{driver.best.value?.toFixed(1)} pts</p>
                  </div>
                )}
                {driver.worst && (
                  <div className="bg-rose-50/70 rounded-xl p-3 space-y-1">
                    <p className="text-[0.6rem] uppercase text-rose-400">Brecha prioritaria</p>
                    <p className="text-xs text-rose-800 font-medium leading-snug">{driver.worst.text}</p>
                    <p className="text-xs text-rose-500 font-bold">{driver.worst.value?.toFixed(1)} pts</p>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch" data-pdf-break="true" data-pdf-source="destinations">
          <div className="card-premium p-6 space-y-5 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Destinos & puertos</p>
                  <p className="text-sm font-semibold text-slate-800">Flujo de exportación</p>
                </div>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">
                {destinationLeaderboard.length} destinos · {portLeaderboard.length} puertos
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] gap-6 flex-1">
              <div className="space-y-4">
                <p className="text-[0.65rem] uppercase text-slate-400 mb-2">Destinos más frecuentes</p>
                <ul className="space-y-3">
                  {destinationLeaderboard.map((item) => {
                    const IconComp = item.IconComp;
                    const percentWidth = Math.min(100, Math.max(0, item.percent || 0));
                    return (
                      <li
                        key={item.label}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                          item.isLeader ? 'border-emerald-200 bg-emerald-50/80 shadow-sm' : 'border-slate-200/70 bg-white'
                        }`}
                      >
                        <span className={`p-2 rounded-xl flex-shrink-0 ${item.accent}`}>
                          <IconComp className="h-4 w-4" />
                        </span>
                        <div className="w-full">
                          <p className="text-sm font-semibold text-slate-800">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.percent}% de envíos</p>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                            <div
                              className={`h-full ${item.isLeader ? 'bg-emerald-500' : 'bg-slate-300'}`}
                              style={{ width: `${percentWidth}%` }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="space-y-4">
                <p className="text-[0.65rem] uppercase text-slate-400 mb-2">Puertos declarados</p>
                <ul className="space-y-3">
                  {portLeaderboard.map((item) => {
                    const IconComp = item.IconComp;
                    const percentWidth = Math.min(100, Math.max(0, item.percent || 0));
                    return (
                      <li
                        key={item.label}
                        className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                          item.isLeader ? 'border-[#004AAD]/20 bg-[#004AAD]/5 shadow-sm' : 'border-slate-200/70 bg-white'
                        }`}
                      >
                        <span className={`p-2 rounded-xl flex-shrink-0 ${item.accent}`}>
                          <IconComp className="h-4 w-4" />
                        </span>
                        <div className="w-full">
                          <p className="text-sm font-semibold text-slate-800">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.percent}% de envíos</p>
                          <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                            <div
                              className={`h-full ${item.isLeader ? 'bg-[#004AAD]' : 'bg-slate-300'}`}
                              style={{ width: `${percentWidth}%` }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
          <div className="card-premium p-6 flex flex-col gap-4 h-full">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Verbatims destacados</p>
                  <p className="text-sm font-semibold text-slate-800">Voz del exportador</p>
                </div>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">{featuredVerbatims.length} citas</span>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              {featuredVerbatims.length > 0 ? (
                featuredVerbatims.map((item, idx) => {
                  const tone = verbatimToneStyles[item.tone] || verbatimToneStyles.neutral;
                  const anonymousLabel = `Exportador ${String(idx + 1).padStart(2, '0')}`;
                  const hasCleanedQuote = Boolean(item.cleanedQuote);
                  return (
                    <div key={`${item.company}-${idx}`} className="rounded-xl border border-slate-200/70 overflow-hidden bg-white">
                      <div className={`h-[3px] ${tone.strip}`} />
                      <div className="p-4 flex gap-3">
                        <span className="text-5xl leading-none font-serif text-slate-200 select-none flex-shrink-0 mt-0.5">{'“'}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-600 leading-relaxed italic break-words">{item.quote}</p>
                          {hasCleanedQuote && (
                            <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[0.62rem] leading-relaxed text-slate-400">
                              <span className="font-semibold text-slate-500">Original preservado:</span> {item.originalQuote}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-3 gap-2">
                            <div>
                              <p className="text-xs font-semibold text-slate-800">{anonymousLabel}</p>
                              <p className="text-[0.62rem] text-slate-400 mt-0.5">Naviera: {item.naviera}</p>
                            </div>
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {hasCleanedQuote && (
                                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-[0.65rem] font-semibold text-slate-500">
                                  Idea clave
                                </span>
                              )}
                              <span className={`px-2.5 py-1 rounded-full text-[0.65rem] font-semibold flex-shrink-0 ${tone.badge}`}>{tone.label}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500">
                  No hay verbatims reales disponibles para este período.
                </div>
              )}
            </div>
          </div>
        </div>

        {qualitativeInsights.length > 0 && (
          <div
            className="card-premium p-6 space-y-5"
            data-pdf-break="true"
            data-pdf-source="qualitative"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <span className="w-1 h-5 rounded-full bg-[#004AAD] flex-shrink-0" />
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">Mapa cualitativo</p>
                  <p className="text-sm font-semibold text-slate-800">Patrones agregados de respuestas abiertas</p>
                </div>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg">{qualitativeInsights.length} patrones</span>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {qualitativeInsights.map((insight) => {
                const tone = qualitativeToneStyles[insight.tone] || qualitativeToneStyles.slate;
                return (
                  <article key={insight.id} className="rounded-xl border border-slate-200/70 bg-white overflow-hidden">
                    <div className={`h-[3px] ${tone.strip}`} />
                    <div className="p-4 space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[0.6rem] uppercase text-slate-400">{insight.confidenceLabel}</p>
                          <h3 className="text-base font-semibold leading-tight text-slate-900">{insight.title}</h3>
                        </div>
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold tabular-nums ${tone.badge}`}>
                          {insight.count} menc.
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed text-slate-600">{insight.summary}</p>
                      <div>
                        <div className="mb-1.5 flex items-center justify-between text-[0.62rem] uppercase text-slate-400">
                          <span>Peso cualitativo</span>
                          <span>{insight.share}%</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.min(100, insight.share)}%` }} />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {insight.topWords.map(([word, count]) => (
                          <span
                            key={`${insight.id}-${word}`}
                            className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[0.65rem] font-semibold text-slate-500"
                          >
                            {word} <span className="opacity-60">· {count}</span>
                          </span>
                        ))}
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-[0.58rem] uppercase text-slate-400">Origen</p>
                          <p className="mt-1 text-xs font-medium leading-snug text-slate-600">
                            {insight.sourceQuestions.join(' · ')}
                          </p>
                        </div>
                        <div className="rounded-lg bg-slate-50 p-3">
                          <p className="text-[0.58rem] uppercase text-slate-400">Acción sugerida</p>
                          <p className="mt-1 text-xs font-medium leading-snug text-slate-600">{insight.action}</p>
                        </div>
                      </div>
                      {insight.navieras.length > 0 && (
                        <p className="text-[0.65rem] leading-snug text-slate-400">
                          Navieras mencionadas: <span className="font-semibold text-slate-500">{insight.navieras.join(', ')}</span>
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}
        </div>
        {/* END OPTIONAL EXECUTIVE PDF PAGE MAP */}
        {reportView === 'extended' && (
          <div className="card-premium p-6 md:p-8 space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-pdf-source="extended-intro">
              <div>
                <p className="text-xs uppercase text-slate-400">Detalle por pregunta</p>
                <h2 className="text-2xl font-semibold text-slate-900">Lectura extendida</h2>
                <p className="text-slate-500">Resultados normalizados (0–100) para cada pregunta del cuestionario.</p>
              </div>
              <span className="text-xs text-slate-400 uppercase">
                {perQuestionSummaries.reduce((acc, dim) => acc + dim.questions.length, 0)} indicadores
              </span>
            </div>
            <div className="space-y-6">
              {perQuestionSummaries.map((dimension) => (
                <div
                  key={dimension.id}
                  className="border border-slate-200/70 rounded-xl p-5 space-y-4"
                  data-pdf-break="true"
                  data-pdf-source={`questions-${dimension.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs uppercase text-slate-400">{DIMENSION_LABEL_MAP[dimension.name] || dimension.name}</p>
                      <h3 className="text-xl font-semibold text-slate-900">{dimension.name}</h3>
                    </div>
                    <span className="text-xs uppercase text-slate-500">Peso {Math.round(dimension.weight * 100)}%</span>
                  </div>
                  <div className="space-y-3">
                    {dimension.questions.map((question) => {
                      const badge = getScoreBadge(question.value);
                      return (
                        <div key={question.id} className="rounded-xl border border-slate-200/70 p-4 space-y-2 bg-white">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-slate-900 break-words leading-snug">{question.text}</p>
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badge.chip}`}>
                              {typeof question.value === 'number' ? `${question.value.toFixed(1)} pts · ${badge.label}` : badge.label}
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div
                              className={`h-full transition-all duration-300 ${badge.bar}`}
                              style={{ width: `${Math.max(0, Math.min(100, question.value ?? 0))}%` }}
                            />
                          </div>
                          {question.interpretation && <p className="text-xs text-slate-500">{question.interpretation}</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicDashboard;
